






























































































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { GET_ORDER, UPDATE_ORDER, CANCEL_ORDER, ORDER_NEW_DATA } from '@/queries/Order';
import {
  formDataToMutationInput,
  modifyOrderData,
  orderHasQuantityDifference,
  populateOrderWithUserNonChangeableCustomerInfo,
  populateOrderWithUserNonChangeableSupplierInfo,
} from '@/app/Order';
import DocumentControls from '../../Shared/DocumentControls.vue';
import { GQL_order_order } from '@/queries/__generated__/order';
import InvoiceNewButton from '../../Invoice/InvoiceNewButton.vue';
import PermissionService from '@/utils/permission-service';
import OrderPdfSection from './OrderPdfSection.vue';
import { GQL_lockOrder, GQL_lockOrder_lockOrder_order } from '@/queries/__generated__/lockOrder';
import { GQL_cancelOrder, GQL_cancelOrder_cancelOrder_order } from '@/queries/__generated__/cancelOrder';
import { GET_CUSTOMER } from '@/queries/Customer';
import { GQL_updateOrder, GQL_updateOrder_updateOrder_order } from '@/queries/__generated__/updateOrder';
import { GQL_duplicateOrder } from '@/queries/__generated__/duplicateOrder';
import gql from 'graphql-tag';
import { Dayjs } from 'dayjs';
import { ORDER_PDF } from './orderPdf';

const LOCK_ORDER = gql`
  mutation lockOrder(
    $id: ID!,
    $createOrderFromDifference: Boolean,
  ) {
    lockOrder(
      input: {
        id: $id,
        createOrderFromDifference: $createOrderFromDifference,
      }
    ) {
      order {
        id
        status
        isOpen
        isLocked
        isCancelled
        lockedAt
        lockedBy {
          id name
        }
      }
    }
  }
`;

const DUPLICATE_ORDER = gql`
  mutation duplicateOrder( $orderId: ID! ) {
    duplicateOrder( input: { orderId: $orderId } ) {
      order {
        id
      }
    }
  }
`;

@Component({
  components: {
    DocumentControls,
    InvoiceNewButton,
    OrderPdfSection,
  },
})
export default class OrderEditControls extends Vue {
  public PermissionService = PermissionService;
  @Prop(Object) public order!: GQL_order_order;
  @Prop(Boolean) public validForm!: boolean;
  @Prop(Boolean) public dirtyForm!: boolean;
  public GET_ORDER = GET_ORDER;
  public UPDATE_ORDER = UPDATE_ORDER;
  public LOCK_ORDER = LOCK_ORDER;
  public DUPLICATE_ORDER = DUPLICATE_ORDER;
  private ORDER_PDF = ORDER_PDF;

  get showDropdown() {
    return (
      this.showDuplicateOrderButton ||
      this.showCancelOrderButton
    );
  }

  get showDuplicateOrderButton() {
    return true;
  }

  // Company setting: number the save / lock / invoice steps, say who did each
  // and when, and ask before saving or locking.
  get actionLogEnabled(): boolean {
    return !!this.order.unit?.unitSetting?.unitInvoiceSetting?.orderSaveLockConfirmation;
  }

  // "mandag 2. november 2026 kl. 20:00, Arnt Telhaug"
  private actionLog(time: Dayjs, user: { name: string } | null | undefined): string {
    return String(this.$t('Order.actionLog', {
      time: this.$dayjs.strict(time).format('LLLL'),
      username: user ? user.name : this.$t('Order.unknownUser'),
    }));
  }

  private confirmSave(mutate: Function): void {
    if (!this.actionLogEnabled) {
      this.save(mutate);
      return;
    }

    this.$sPopup.confirm(``, {
      title: this.$t('PopupTitle.confirmSaveOrder'),
      message: this.$t('PopupBody.confirmSaveOrder', { number: this.order.number }),
      onConfirm: () => this.save(mutate),
    });
  }

  private confirmLock(): void {
    if (!this.actionLogEnabled) {
      this.lockOrder();
      return;
    }

    this.$sPopup.confirm(``, {
      title: this.$t('PopupTitle.confirmLockOrder'),
      message: this.$t('PopupBody.confirmLockOrder', { number: this.order.number }),
      onConfirm: () => this.lockOrder(),
    });
  }

  get showCancelOrderButton() {
    return (
      !this.order.invoice &&
      !this.order.isCancelled
    );
  }

  public resetFormDirty() {
    // We might have some watchers in action since we update a lot of data after the mutations,
    // ResetFormDirty-event should be emitted after the watchers are done.
    setTimeout(() => this.$emit(`reset-form-dirty`), 0);
  }

  public cancelOrder() {
    this.$apollo.mutate<GQL_cancelOrder>({
      mutation: CANCEL_ORDER,
      variables: { orderId: this.order.id },
    }).then(({ data }) => {
      if (!data?.cancelOrder?.order) return;
      Object.assign<GQL_order_order, GQL_cancelOrder_cancelOrder_order>(this.order, data.cancelOrder.order);
      this.$sMessage.success(this.$t('Order.cancelSuccess'));
      this.resetFormDirty();
    });
  }

  private duplicateOrder(): void {
    this.$apollo.mutate<GQL_duplicateOrder>({
      mutation: DUPLICATE_ORDER,
      variables: { orderId: this.order.id },
    }).then(({ data }) => {
      if (!data?.duplicateOrder?.order) return;
      this.$router.push(`/orders/${data.duplicateOrder.order.id}`);
      this.$sMessage.success(this.$t('Order.duplicateSuccess'));
    });
  }

  private lockOrder(): void {
    const mutate = (createOrderFromDifference: boolean): void => {
      const variables = {
        id: this.order.id,
        createOrderFromDifference: createOrderFromDifference,
      };

      this.$apollo.mutate<GQL_lockOrder>({
        mutation: LOCK_ORDER,
        variables,
      }).then(({ data }) => {
        if (!data?.lockOrder?.order) return;
        Object.assign<GQL_order_order, GQL_lockOrder_lockOrder_order>(this.order, data.lockOrder.order);
        this.$sMessage.success(this.$t('Message.lockSuccess'));
        this.resetFormDirty();
      });
    };

    if(orderHasQuantityDifference(this.order)) {
      this.$sPopup.alert(``, {
        title: this.$t('PopupTitle.beforeLocking'),
        message: this.$t('PopupBody.quantityDiff'),
        buttons: [
          {
            text: this.$t('PopupButton.yesMakeNew'),
            color: 'primary',
            click: () => mutate(true),
          },
          {
            text: this.$t('no'),
            click: () => mutate(false),
          },
        ],
      });
    } else {
      mutate(false);
    }
  }

  private refreshParties() {
    this.$apollo.query({
      query: ORDER_NEW_DATA,
      fetchPolicy: 'no-cache',
    }).then(({ data }) => {
      populateOrderWithUserNonChangeableSupplierInfo(this.order, data.currentUser.currentUnit);
    });

    if (this.order.customer) {
      this.$apollo.query({
        query: GET_CUSTOMER,
        variables: { id: this.order.customer.id },
        fetchPolicy: 'network-only',
      }).then(({ data }) => {
        populateOrderWithUserNonChangeableCustomerInfo(this.order, data.customer);
      });
    }
  }

  private orderUpdated({ data }: { data: GQL_updateOrder}) {
    if (!data.updateOrder?.order) return;
    Object.assign<GQL_order_order, GQL_updateOrder_updateOrder_order>(this.order, modifyOrderData(data.updateOrder.order));
    this.$sMessage.success(this.$t('Message.saveSuccess'));
    this.resetFormDirty();
  }

   private save(mutate: Function) {
    this.$emit(`save-button-click`);
    // We want to remove empty lines before saving.
    // However if we remove empty lines we trigger calculations to happen
    // in the next tick. This is why delay mutating by doing it in setTimeout.
    // This makes it be called after the calculations.
    setTimeout(() => {
      mutate({ variables: formDataToMutationInput({ order: this.order }) });
    }, 0);
  }
}
